import FieldRow from './FieldRow'
import { DerivedValueBox, EditableValueBox } from './ValueBox'
import { formatMoney } from './format'

// Panel spec 3.2/3.3/4: 1px border, rx 8, white fill, 16 top/bottom + 24 left/right padding, full
// content width, 40 row pitch, no info icon on any row. Border `#DDE5DA` (Cong, letter 104 1.1.3;
// was the export's `#E3E9E0`), no brand.* token, so an arbitrary-value class like InterestDropdown's.
//
// Cash vs loan (spec 4, D11): loan draws 6 rows (Sale Price, Loan Payoff, Sale Closing Costs,
// Property Taxes Paid Separately, Rehab Funds Drawn, Estimated Sale Proceeds), cash drops Loan
// Payoff and Rehab Funds Drawn. Gated on `scenarioType`, the same field the screen's other
// gates already use (isLoanScenario: SellingCostsSection.jsx, HoldingCostsSection.jsx) -- no proxy.
//
// Sale Price reads `arv` directly, not `computed` (spec 5, README.md "the computed prop contract"):
// it is the same input value ARV shows elsewhere on the screen, not a derived figure, so it is not
// gated on `computed` being populated. Every other row here IS gated on `computed` and renders the
// placeholder until a read-time endpoint exists (pages/Flip.jsx:145-159).
export default function SalesProceedsPanel({ scenarioType, scenario, computed, onFieldChange }) {
  const s = scenario ?? {}
  const isLoan = scenarioType === 'loan'

  // arv is a number or null in state (pages/Flip.jsx, scenarioNumbers.js); formatMoney turns a
  // cleared (null) ARV into the placeholder rather than a real "0".
  const salePrice = formatMoney(s.arv)

  // br4_est_payoff is returned signed and the backend subtracts it signed when computing
  // estSaleProceeds -- Math.abs here would show a payoff that does not reconcile with the total
  // for a negative payoff (loan amount < rehab costs, reachable). Shown as returned, unlike
  // Refinance Summary's own Existing Loan Payoff row, which stands alone rather than summing
  // against a displayed total and applies Math.abs (RefinanceSummarySection.jsx, README.md) --
  // that one reports its own inconsistency instead of being changed here.
  const rawPayoff = computed?.br4_est_payoff
  const loanPayoff = Number.isFinite(rawPayoff) ? formatMoney(rawPayoff) : null

  const saleClosingCosts = formatMoney(computed?.saleClosingCosts)
  const estSaleProceeds = formatMoney(computed?.estSaleProceeds)

  return (
    <div className="mt-[8px] w-full rounded-[8px] bg-white pt-[16px] pb-[16px] px-[24px] flex flex-col border border-[#DDE5DA]">
      <FieldRow label="Sale Price">
        <DerivedValueBox value={salePrice} ariaLabel="Sale Price" />
      </FieldRow>

      {isLoan ? (
        <div className="mt-[8px]">
          <FieldRow label="Loan Payoff">
            <DerivedValueBox value={loanPayoff} ariaLabel="Loan Payoff" />
          </FieldRow>
        </div>
      ) : null}

      <div className="mt-[8px]">
        <FieldRow label="Sale Closing Costs">
          <DerivedValueBox value={saleClosingCosts} ariaLabel="Sale Closing Costs" />
        </FieldRow>
      </div>

      {/* D8: "Property Taxes Paid Separately" in both versions -- the design's two frames spell
          this differently (loan vs "Property Taxes (Paid Separately)" cash); both workbook sheets
          and the loan frame agree on this spelling, so it is used for both rather than left to
          differ between panels that share every other label. */}
      <div className="mt-[8px]">
        <FieldRow label="Property Taxes Paid Separately">
          <EditableValueBox money
            value={String(s.tax_out_of_pocket ?? '')}
            onChange={(v) => onFieldChange('tax_out_of_pocket', v)}
            ariaLabel="Property Taxes Paid Separately"
          />
        </FieldRow>
      </div>

      {isLoan ? (
        <div className="mt-[8px]">
          <FieldRow label="Rehab Funds Drawn">
            <EditableValueBox money
              value={String(s.drawn_funds ?? '')}
              onChange={(v) => onFieldChange('drawn_funds', v)}
              ariaLabel="Rehab Funds Drawn"
            />
          </FieldRow>
        </div>
      ) : null}

      <div className="mt-[8px]">
        <FieldRow label="Estimated Sale Proceeds" weight="bold">
          <DerivedValueBox value={estSaleProceeds} bold topDivider ariaLabel="Estimated Sale Proceeds" />
        </FieldRow>
      </div>
    </div>
  )
}
